/**
 * notificationStore.js
 *
 * Persistent per-user notification store backed by localStorage.
 * Used to deliver assignment/unassignment change alerts to radiologists
 * even when they were not online when the admin action took place.
 *
 * Storage layout:
 *   localStorage['afya-worklist-notifications'] = {
 *     "<keycloak-user-id>": [ ...NotificationRecord ],
 *   }
 *
 * NotificationRecord fields:
 *   id          – unique string (e.g. "unassign-<uid>-<ts>")
 *   type        – 'assigned' | 'unassigned' | 'reassigned'
 *   studyInstanceUID
 *   patientName
 *   bodyPart
 *   modality
 *   studyDate   – optional, display string
 *   timestamp   – ISO string (when the event was written)
 *   expiresAt   – ISO string (TTL_MS after timestamp)
 *   dismissed   – boolean
 */

const STORAGE_KEY = 'afya-worklist-notifications';
// Key used to signal other tabs that the store changed
const UPDATED_KEY = 'afya-worklist-notifications-updated';
// Notifications expire after 48 h
const TTL_MS = 48 * 60 * 60 * 1000;

/**
 * Write a notification addressed to a specific Keycloak user ID.
 * Automatically stamps timestamp, expiresAt and dismissed fields.
 *
 * @param {string} userId  – target radiologist's Keycloak ID
 * @param {object} notification – { id, type, studyInstanceUID, patientName, bodyPart, modality, studyDate? }
 */
export function writeNotification(userId, notification) {
  if (!userId || !notification?.id) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const store = raw ? JSON.parse(raw) : {};
    const now = new Date();

    // Prune already-expired entries for this user
    const existing = (store[userId] || []).filter(
      (n) => !n.expiresAt || new Date(n.expiresAt) > now,
    );

    // Avoid duplicates — skip if an identical id already exists
    if (existing.some((n) => n.id === notification.id)) return;

    existing.push({
      ...notification,
      timestamp: now.toISOString(),
      expiresAt: new Date(now.getTime() + TTL_MS).toISOString(),
      dismissed: false,
    });

    // Cap per-user list at 100 entries (oldest first, so slice from end)
    store[userId] = existing.slice(-100);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));

    // Ping other open tabs (storage event only fires in OTHER windows/tabs,
    // so this is specifically for cross-tab real-time delivery).
    localStorage.setItem(UPDATED_KEY, JSON.stringify({ userId, ts: Date.now() }));
  } catch (e) {
    console.error('[notificationStore] write error:', e);
  }
}

/**
 * Return all non-expired notification records for a user (including dismissed ones).
 * Callers are responsible for filtering dismissed ones if needed.
 */
export function readNotifications(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const store = JSON.parse(raw);
    const now = new Date();
    return (store[userId] || []).filter(
      (n) => !n.expiresAt || new Date(n.expiresAt) > now,
    );
  } catch (e) {
    console.error('[notificationStore] read error:', e);
    return [];
  }
}

/**
 * Mark a notification as dismissed (persists across page reloads).
 */
export function dismissNotification(userId, notifId) {
  if (!userId || !notifId) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const store = raw ? JSON.parse(raw) : {};
    if (store[userId]) {
      store[userId] = store[userId].map((n) =>
        n.id === notifId ? { ...n, dismissed: true } : n,
      );
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
  } catch (e) {
    console.error('[notificationStore] dismiss error:', e);
  }
}

/**
 * The localStorage key to watch in storage events so WorklistPage can
 * detect new notifications written by admin dialogs in the same browser.
 */
export const NOTIFICATION_UPDATED_KEY = UPDATED_KEY;
